import React from 'react';
import styles from './profile.module.css';
import { Button, Input } from 'glintly-ui';

const ProfileDetails = ({
  user,
  isEditing,
  setIsEditing,
  editForm,
  handleEditChange,
  handleSave,
  handleCancel,
}) => {
  return (
    <div className={styles.personalInfo}>
      <div className={styles.sectionHeader}>
        <h2>Personal Information</h2>
        <Button
          variant={isEditing ? 'outline' : 'primary'}
          className={styles.editBtn}
          onClick={() => setIsEditing(!isEditing)}
        >
          {isEditing ? 'Cancel' : 'Edit Profile'}
        </Button>
      </div>

      <div className={styles.infoCard}>
        {isEditing ? (
          <div className={styles.editForm}>
            {/* Basic Information Section */}
            <div className={styles.formSection}>
              <h3 className={styles.sectionTitle}>Basic Information</h3>
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <Input.Root name="name">
                    <Input.Label>Full Name</Input.Label>
                    <Input.Control
                      type="text"
                      value={editForm.name}
                      onChange={handleEditChange}
                      placeholder="Enter your full name"
                    />
                  </Input.Root>
                </div>
                <div className={styles.formGroup}>
                  <Input.Root name="profileImg">
                    <Input.Label>Profile Image URL</Input.Label>
                    <Input.Control
                      type="url"
                      value={editForm.profileImg}
                      onChange={handleEditChange}
                      placeholder="Enter profile image URL"
                    />
                  </Input.Root>
                </div>
              </div>
            </div>

            {/* Contact Information Section */}
            <div className={styles.formSection}>
              <h3 className={styles.sectionTitle}>Contact Information</h3>
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <Input.Root name="email">
                    <Input.Label>Email Address</Input.Label>
                    <Input.Control
                      type="email"
                      value={editForm.email}
                      onChange={handleEditChange}
                      disabled
                      placeholder="Email cannot be changed"
                    />
                  </Input.Root>
                </div>
                <div className={styles.formGroup}>
                  <Input.Root name="mobile">
                    <Input.Label>Primary Mobile</Input.Label>
                    <Input.Control
                      type="tel"
                      value={editForm.mobile}
                      onChange={handleEditChange}
                      placeholder="Enter primary mobile number"
                    />
                  </Input.Root>
                </div>
              </div>
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <Input.Root name="secMobile">
                    <Input.Label>Secondary Mobile (Optional)</Input.Label>
                    <Input.Control
                      type="tel"
                      value={editForm.secMobile}
                      onChange={handleEditChange}
                      placeholder="Enter secondary mobile number"
                    />
                  </Input.Root>
                </div>
                <div className={styles.formGroup}>
                  <Input.Root name="accountType">
                    <Input.Label>Account Type</Input.Label>
                    <Input.Control
                      type="text"
                      value={user?.provider === 'google' ? 'Google Account' : 'Local Account'}
                      disabled
                      placeholder="Account type"
                    />
                  </Input.Root>
                </div>
              </div>
            </div>

            {/* Address Information Section */}
            <div className={styles.formSection}>
              <h3 className={styles.sectionTitle}>Address Information</h3>
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <Input.Root name="address.fullAddress">
                    <Input.Label>Full Address</Input.Label>
                    <Input.Control
                      type="text"
                      value={editForm.address.fullAddress}
                      onChange={handleEditChange}
                      placeholder="Enter your full address"
                    />
                  </Input.Root>
                </div>
                <div className={styles.formGroup}>
                  <Input.Root name="address.landmark">
                    <Input.Label>Landmark</Input.Label>
                    <Input.Control
                      type="text"
                      value={editForm.address.landmark}
                      onChange={handleEditChange}
                      placeholder="Enter nearby landmark"
                    />
                  </Input.Root>
                </div>
              </div>
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <Input.Root name="address.city">
                    <Input.Label>City</Input.Label>
                    <Input.Control
                      type="text"
                      value={editForm.address.city}
                      onChange={handleEditChange}
                      placeholder="Enter your city"
                    />
                  </Input.Root>
                </div>
                <div className={styles.formGroup}>
                  <Input.Root name="address.postalCode">
                    <Input.Label>Postal Code</Input.Label>
                    <Input.Control
                      type="text"
                      value={editForm.address.postalCode}
                      onChange={handleEditChange}
                      placeholder="Enter postal code"
                    />
                  </Input.Root>
                </div>
              </div>
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <Input.Root name="address.country">
                    <Input.Label>Country</Input.Label>
                    <Input.Control
                      type="text"
                      value={editForm.address.country}
                      onChange={handleEditChange}
                      placeholder="Enter your country"
                    />
                  </Input.Root>
                </div>
              </div>
            </div>

            <div className={styles.formActions}>
              <Button variant="primary" className={styles.saveBtn} onClick={handleSave}>
                Save Changes
              </Button>
              <Button variant="gray" className={styles.cancelBtn} onClick={handleCancel}>
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <div className={styles.infoDisplay}>
            {/* Basic Information Display */}
            <div className={styles.infoSection}>
              <h3 className={styles.sectionTitle}>Basic Information</h3>
              <div className={styles.infoGrid}>
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>Full Name</span>
                  <span className={styles.infoValue}>{user?.name || 'Not provided'}</span>
                </div>
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>Email</span>
                  <span className={styles.infoValue}>{user?.email || 'Not provided'}</span>
                </div>
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>Account Type</span>
                  <span className={styles.infoValue}>
                    {user?.provider === 'google' ? 'Google Account' : 'Local Account'}
                  </span>
                </div>
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>User Role</span>
                  <span className={styles.infoValue}>
                    {user?.role === 'admin'
                      ? 'Administrator'
                      : user?.role === 'vender'
                        ? 'Vendor'
                        : 'Customer'}
                  </span>
                </div>
              </div>
            </div>

            {/* Contact Information Display */}
            <div className={styles.infoSection}>
              <h3 className={styles.sectionTitle}>Contact Information</h3>
              <div className={styles.infoGrid}>
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>Primary Mobile</span>
                  <span className={styles.infoValue}>{user?.mobile || 'Not provided'}</span>
                </div>
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>Secondary Mobile</span>
                  <span className={styles.infoValue}>{user?.secMobile || 'Not provided'}</span>
                </div>
              </div>
            </div>

            {/* Address Information Display */}
            <div className={styles.infoSection}>
              <h3 className={styles.sectionTitle}>Address Information</h3>
              <div className={styles.infoGrid}>
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>Full Address</span>
                  <span className={styles.infoValue}>
                    {user?.address?.fullAddress || 'Not provided'}
                  </span>
                </div>
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>Landmark</span>
                  <span className={styles.infoValue}>
                    {user?.address?.landmark || 'Not provided'}
                  </span>
                </div>
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>City</span>
                  <span className={styles.infoValue}>{user?.address?.city || 'Not provided'}</span>
                </div>
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>Postal Code</span>
                  <span className={styles.infoValue}>
                    {user?.address?.postalCode || 'Not provided'}
                  </span>
                </div>
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>Country</span>
                  <span className={styles.infoValue}>
                    {user?.address?.country || 'Not provided'}
                  </span>
                </div>
              </div>
            </div>

            {/* Account Status */}
            <div className={styles.infoSection}>
              <h3 className={styles.sectionTitle}>Account Status</h3>
              <div className={styles.infoGrid}>
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>Account Status</span>
                  <span
                    className={`${styles.infoValue} ${styles.statusBadge} ${user?.status ? styles.active : styles.inactive}`}
                  >
                    {user?.status ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>Member Since</span>
                  <span className={styles.infoValue}>
                    {user?.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'Unknown'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfileDetails;
